import React, { useRef, useEffect, forwardRef, useImperativeHandle, useState } from 'react';

/**
 * A text input component that maintains focus and cursor position across re-renders
 * This improved version uses a local state to prevent flickering and input resets
 */
const PersistentInput = forwardRef(({
  value,
  onChange,
  onKeyPress,
  placeholder,
  disabled,
  className,
  isUserTyping = false
}, ref) => {
  // Internal ref for the input element
  const inputRef = useRef(null);

  // Use local state to prevent flickering
  const [localValue, setLocalValue] = useState(value);

  // Track if the user is actively typing
  const isTypingRef = useRef(false);

  // Track cursor position and selection
  const cursorPositionRef = useRef({
    start: 0,
    end: 0,
    wasFocused: false
  });

  // Update local value when prop value changes, but only if not typing
  useEffect(() => {
    if (!isTypingRef.current && value !== localValue) {
      setLocalValue(value);
    }
  }, [value, localValue]);

  // Update isTypingRef when isUserTyping prop changes
  useEffect(() => {
    isTypingRef.current = isUserTyping;
  }, [isUserTyping]);

  // Expose methods to parent components
  useImperativeHandle(ref, () => ({
    focus: () => {
      if (inputRef.current) {
        inputRef.current.focus();
      }
    },
    blur: () => {
      if (inputRef.current) {
        inputRef.current.blur();
      }
    },
    get element() {
      return inputRef.current;
    },
    get isTyping() {
      return isTypingRef.current;
    }
  }));

  // Handle local changes and propagate to parent
  const handleChange = (e) => {
    // Mark as typing
    isTypingRef.current = true;

    const newValue = e.target.value;

    // Update local value immediately for responsive UI
    setLocalValue(newValue);

    // Propagate to parent immediately for chat responsiveness
    onChange(e);
  };

  // Handle key press events
  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      // Mark as not typing when Enter is pressed
      isTypingRef.current = false;
    }

    // Call the parent handler
    if (onKeyPress) {
      onKeyPress(e);
    }
  };

  // Save cursor position and focus state before render
  useEffect(() => {
    if (inputRef.current) {
      const input = inputRef.current;
      const wasFocused = document.activeElement === input;

      if (wasFocused) {
        cursorPositionRef.current = {
          start: input.selectionStart,
          end: input.selectionEnd,
          wasFocused: true
        };
      }
    }
  });

  // Restore cursor position and focus after render
  useEffect(() => {
    if (inputRef.current && cursorPositionRef.current.wasFocused) {
      const input = inputRef.current;
      input.focus();

      // Only set selection range if we have valid positions
      if (
        cursorPositionRef.current.start !== undefined &&
        cursorPositionRef.current.end !== undefined
      ) {
        try {
          input.setSelectionRange(
            cursorPositionRef.current.start,
            cursorPositionRef.current.end
          );
        } catch (e) {
          console.error('Error setting selection range:', e);
        }
      }

      // Reset the flag
      cursorPositionRef.current.wasFocused = false;
    }
  });

  // Use the local value instead of the prop value
  return (
    <input
      ref={inputRef}
      type="text"
      value={localValue}
      onChange={handleChange}
      onKeyPress={handleKeyPress}
      placeholder={placeholder}
      disabled={disabled}
      className={className}
    />
  );
});

PersistentInput.displayName = 'PersistentInput';

export default PersistentInput;
